import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, Download, File as FileSvg, FolderOpenIcon, Upload, FolderPlus, Eye, Loader2, ExternalLink, CheckSquare, Square, X, FileArchive } from 'lucide-react';
import '../styles/SharedViewer.css';
import Modal from '../modules/Modal';
import { DocumentViewer } from '../modules/DocumentViewer';
import { useNavVariant } from '../App';
import { collectDroppedDocumentFiles } from '../utils/documentUploadFiles';
import {
    getSharedViewerApiEndpoint,
    prepareSharedViewerUploadFiles,
    uploadSharedViewerFiles,
} from '../services/sharedViewerUploadService';
import {
    reconcileSharedViewerData,
    sharedViewerRowIds,
} from '../utils/liveDataReconciliation';

const SHARED_VIEWER_API_ENDPOINT = getSharedViewerApiEndpoint();
const DOCUMENTS_API_ENDPOINT = SHARED_VIEWER_API_ENDPOINT.replace(/\/share\.php$/, '/documents.php');
export const SHARED_VIEWER_LIVE_POLL_MS = 20000;
export const SHARED_VIEWER_LIVE_REQUEST_TIMEOUT_MS = 15000;

const isTerminalShareFailure = (status, data) => {
    const numericStatus = Number(status || 0);
    // Request timeout, Too Early, and rate limiting are retryable even though
    // they live in the 4xx range. Other client errors mean the token, path, or
    // authorization state is no longer usable and stale inventory must go.
    if (
        numericStatus >= 400
        && numericStatus < 500
        && ![408, 425, 429].includes(numericStatus)
    ) return true;
    if (data?.terminal === true) return true;

    const message = String(data?.message || '');
    return /invalid|expired|revoked|not found|does not exist|unauthori[sz]ed|forbidden|cannot be shared|outside (?:the )?shared scope|historical files?.*not available/i.test(message);
};

const SharedViewer = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shareData, setShareData] = useState(null);
    const [currentPath, setCurrentPath] = useState('');
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [addMargin, setAddMargin] = useState(false);
    // Selection / bulk actions
    const [selectedItems, setSelectedItems] = useState([]); // array of {id,type,'file'|'folder', name, url?, size?}
    const [nameSortDirection, setNameSortDirection] = useState(null);
    const [buildingZip, setBuildingZip] = useState(false);
    const [zipProgress, setZipProgress] = useState(0); // 0-100
    const [zipStatus, setZipStatus] = useState(''); // Status message for ZIP creation
    const [zipReadyDownload, setZipReadyDownload] = useState(null);
    const [passwordPrompt, setPasswordPrompt] = useState(false);
    const [passwordValue, setPasswordValue] = useState('');
    const [badPassword, setBadPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    
    // File preview states
    const [showDocumentViewer, setShowDocumentViewer] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const navVariant = useNavVariant();
    
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const zipPollGenerationRef = useRef(0);
    const unlockInFlightRef = useRef(false);
    const shareRevisionRef = useRef('');
    const shareRequestGenerationRef = useRef(0);
    const shareRequestScopeRef = useRef({ token, path: currentPath });
    shareRequestScopeRef.current = { token, path: currentPath };

    useEffect(() => () => {
        zipPollGenerationRef.current += 1;
        shareRequestGenerationRef.current += 1;
    }, []);

    useEffect(() => {
        zipPollGenerationRef.current += 1;
        shareRevisionRef.current = '';
        setBuildingZip(false);
        setZipProgress(0);
        setZipStatus('');
        setZipReadyDownload(null);
    }, [token]);
    
    const fetchShareData = useCallback(async (opts = {}) => {
        const unlocking = typeof opts.password === 'string' && opts.password.length > 0;
        if (unlocking && unlockInFlightRef.current) return null;
        if (unlocking) unlockInFlightRef.current = true;
        const background = opts.background === true;
        const requestToken = token;
        const requestPath = currentPath;
        const requestGeneration = shareRequestGenerationRef.current + 1;
        shareRequestGenerationRef.current = requestGeneration;
        const requestIsCurrent = () => {
            const activeScope = shareRequestScopeRef.current;
            return requestGeneration === shareRequestGenerationRef.current
                && activeScope.token === requestToken
                && activeScope.path === requestPath
                && !opts.signal?.aborted;
        };
        if (!background) {
            shareRevisionRef.current = '';
            setLoading(true);
            setError(null);
        }
        const clearCurrentShare = ({ passwordRequired = false, message = '', passwordMessage = '' } = {}) => {
            shareRevisionRef.current = '';
            zipPollGenerationRef.current += 1;
            setShareData(null);
            setSelectedItems([]);
            setBreadcrumbs([]);
            setShowDocumentViewer(false);
            setCurrentFile(null);
            setFilePreview(null);
            setBuildingZip(false);
            setZipProgress(0);
            setZipStatus('');
            setZipReadyDownload(null);
            setPasswordPrompt(passwordRequired);
            setPasswordError(passwordRequired ? passwordMessage : '');
            if (!passwordRequired) {
                setBadPassword(false);
                setError(message || 'This shared link is no longer available.');
            } else {
                setError(null);
            }
            setLoading(false);
        };
        let responseStatus = 0;
        try {
            if (unlocking) {
                const unlockResponse = await fetch(SHARED_VIEWER_API_ENDPOINT, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    cache: 'no-store',
                    signal: opts.signal,
                    body: JSON.stringify({
                        action: 'unlock',
                        token: requestToken,
                        path: requestPath,
                        password: opts.password,
                    }),
                });
                responseStatus = Number(unlockResponse.status || 0);
                const unlockData = await unlockResponse.json();
                if (!requestIsCurrent()) return null;
                if (responseStatus === 403 && unlockData.password_required) {
                    clearCurrentShare({
                        passwordRequired: true,
                        passwordMessage: unlockData.message || 'Incorrect password. Try again.',
                    });
                    setBadPassword(true);
                    return null;
                }
                if (!unlockResponse.ok || !unlockData.success) {
                    const unlockError = new Error(unlockData.message || 'Unable to unlock shared content.');
                    unlockError.terminalShareFailure = isTerminalShareFailure(responseStatus, unlockData);
                    throw unlockError;
                }
                setPasswordValue('');
                setPasswordError('');
            }

            const pollParam = background ? '&poll=1' : '';
            const revisionParam = background && shareRevisionRef.current
                ? `&revision=${encodeURIComponent(shareRevisionRef.current)}`
                : '';
            const response = await fetch(`${SHARED_VIEWER_API_ENDPOINT}?token=${encodeURIComponent(requestToken)}&path=${encodeURIComponent(requestPath)}${pollParam}${revisionParam}`, {
                credentials: 'include', // Include session cookies
                cache: 'no-store',
                signal: opts.signal,
            });
            responseStatus = Number(response.status || 0);
            const data = await response.json();
            // AbortController is advisory: a fetch implementation (or a response
            // already being decoded) can still settle after navigation. Keep all
            // state and revision commits behind the request scope/generation.
            if (!requestIsCurrent()) return null;
            if (data.password_required) {
                clearCurrentShare({ passwordRequired: true, passwordMessage: data.message || '' });
                setBadPassword(false);
                if (unlocking) {
                    setError('Your browser did not retain the shared-link session. Allow cookies for this site, then try again.');
                }
                return;
            }
            if (!response.ok || !data.success) {
                const requestError = new Error(data.message || "Failed to load shared content");
                requestError.terminalShareFailure = isTerminalShareFailure(responseStatus, data);
                throw requestError;
            }
            if (data.content_revision) {
                shareRevisionRef.current = data.content_revision;
            }
            if (data.not_modified) {
                return data;
            }
            // Successful unlock (if previously password protected)
            setPasswordPrompt(false);
            setBadPassword(false);
            setPasswordError('');
            
            // Check logged-in status from the response
            setAddMargin(Boolean(data.is_logged_in || process.env.NODE_ENV === 'development'));
            
            setShareData((current) => (
                background ? reconcileSharedViewerData(current, data) : data
            ));
            
            if (currentPath) {
                const parts = currentPath.split('/').filter(Boolean);
                const crumbs = parts.map((part, index) => {
                    const path = parts.slice(0, index + 1).join('/');
                    return { name: part, path };
                });
                
                setBreadcrumbs([{ name: data.root_name || 'Root', path: '' }, ...crumbs]);
            } else {
                setBreadcrumbs([{ name: data.root_name || 'Root', path: '' }]);
            }
            return data;
        } catch (error) {
            if (error?.name === 'AbortError') return null;
            if (!requestIsCurrent()) return null;
            const terminalShareFailure = error?.terminalShareFailure === true
                || isTerminalShareFailure(responseStatus, null);
            if (unlocking) {
                clearCurrentShare({ passwordRequired: true });
                setBadPassword(false);
                setError(error.message || 'Unable to unlock shared content.');
            } else if (terminalShareFailure) {
                clearCurrentShare({ message: error.message });
            } else if (background) {
                console.warn('Unable to refresh shared content:', error);
            } else {
                setError(error.message);
            }
            return null;
        } finally {
            if (unlocking) unlockInFlightRef.current = false;
            if (!background && requestIsCurrent()) setLoading(false);
        }
    }, [token, currentPath]);

    useEffect(() => {
        const controller = new AbortController();
        fetchShareData({ signal: controller.signal });
        return () => controller.abort();
    }, [fetchShareData]);

    useEffect(() => {
        if (!shareData?.success || !shareData?.is_folder) return undefined;

        let stopped = false;
        let timerId = null;
        let activePoll = null;

        const schedule = (delay = SHARED_VIEWER_LIVE_POLL_MS) => {
            if (stopped || document.hidden) return;
            window.clearTimeout(timerId);
            timerId = window.setTimeout(poll, delay);
        };

        const cancelActivePoll = () => {
            const ownedPoll = activePoll;
            if (!ownedPoll) return;
            activePoll = null;
            window.clearTimeout(ownedPoll.timeoutId);
            ownedPoll.controller.abort();
            ownedPoll.finish('cancelled');
        };

        const poll = async () => {
            if (stopped || document.hidden || uploading) {
                schedule();
                return;
            }

            // A duplicate visibility event must not supersede a healthy poll.
            // Each run owns its controller/timer and only that run may clear
            // them or schedule the next request.
            if (activePoll) return;
            const controller = new AbortController();
            let finish;
            const boundary = new Promise((resolve) => {
                finish = resolve;
            });
            const ownedPoll = {
                controller,
                finish,
                timeoutId: null,
            };
            activePoll = ownedPoll;
            ownedPoll.timeoutId = window.setTimeout(() => {
                if (activePoll !== ownedPoll) return;
                controller.abort();
                finish('timeout');
            }, SHARED_VIEWER_LIVE_REQUEST_TIMEOUT_MS);

            const request = fetchShareData({ background: true, signal: controller.signal });
            await Promise.race([request, boundary]);
            if (activePoll !== ownedPoll) return;
            window.clearTimeout(ownedPoll.timeoutId);
            activePoll = null;
            schedule();
        };

        const handleVisibilityChange = () => {
            window.clearTimeout(timerId);
            if (document.hidden) {
                cancelActivePoll();
                return;
            }
            poll();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        schedule();

        return () => {
            stopped = true;
            window.clearTimeout(timerId);
            cancelActivePoll();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchShareData, shareData?.success, shareData?.is_folder, uploading]);

    // Clear selection when navigating folders, and prune rows removed by a
    // background refresh so bulk actions never target stale inventory.
    useEffect(() => {
        setSelectedItems([]);
    }, [currentPath]);

    useEffect(() => {
        if (!shareData?.is_folder) return;
        const currentIds = sharedViewerRowIds(shareData, currentPath);
        setSelectedItems((items) => {
            const retained = items.filter((item) => currentIds.has(item.id));
            return retained.length === items.length ? items : retained;
        });
    }, [shareData, currentPath]);

    // Auto-preview single files when loaded
    useEffect(() => {
        if (shareData && !shareData.is_folder && shareData.single_file && !filePreview && !previewLoading) {
            handleFilePreview(shareData.single_file);
        }
    }, [shareData, filePreview, previewLoading]);
    
    const handleFolderClick = (folderName) => {
        const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
        setCurrentPath(newPath);
    };
    
    const handleBreadcrumbClick = (path) => {
        setCurrentPath(path);
    };
    
    const handleFileDownload = async (fileUrl, fileName) => {
        try {
            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Download error:", error);
        }
    };

    const renderSharedFileTypeBadge = (fileType) => {
        const normalizedType = String(fileType || 'file').toUpperCase();
        const isArchive = ['ZIP', 'RAR', '7Z', 'TAR', 'GZ'].includes(normalizedType);

        return (
            <div className="shared-file-type-card" aria-label={`${normalizedType} file`}>
                <div className="library-file-extension shared-file-type-badge">
                    {isArchive ? <FileArchive size={32} /> : <FileSvg size={32} />}
                    <span>{normalizedType || 'FILE'}</span>
                </div>
            </div>
        );
    };

    const renderUnsupportedFilePreview = (fileType, message = 'Preview is not available for this file type.') => (
        <div className="generic-file-preview">
            {renderSharedFileTypeBadge(fileType)}
            <p>{message}</p>
        </div>
    );

    const openFileViewer = (file) => {
        setCurrentFile({ ...file, url: file.url, open_url: file.open_url, download_url: file.download_url, allowDirectOpenFallback: false });
        setShowDocumentViewer(true);
    };

    const getPreviewUrl = (file) => file?.open_url || file?.url;
    const getDownloadUrl = (file) => file?.download_url || file?.open_url || file?.url;
    
    const uploadFilesToSharedFolder = async (files) => {
        const uploadFiles = prepareSharedViewerUploadFiles(files);
        if (uploadFiles.length === 0) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            await uploadSharedViewerFiles({
                files: uploadFiles,
                token,
                path: currentPath,
                onProgress: setUploadProgress,
            });
            await fetchShareData();
        } catch (error) {
            console.error('Upload error:', error);
            alert('Upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleUploadPickerChange = (event) => {
        // FileList is live. Snapshot it before clearing the input so every
        // selected item survives and the same selection can be chosen again.
        const files = Array.from(event.currentTarget.files || []);
        event.currentTarget.value = '';
        return uploadFilesToSharedFolder(files);
    };

    const handleSharedDragEnter = (event) => {
        if (!shareData?.allow_upload) return;
        event.preventDefault();
        event.stopPropagation();
        if (uploading) return;
        dragDepthRef.current += 1;
        setDragActive(true);
    };

    const handleSharedDragOver = (event) => {
        if (!shareData?.allow_upload) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleSharedDragLeave = (event) => {
        if (!shareData?.allow_upload) return;
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragActive(false);
    };

    const handleSharedDrop = async (event) => {
        if (!shareData?.allow_upload) return;
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = 0;
        setDragActive(false);
        if (uploading) return;

        // The collector snapshots every DataTransfer handle synchronously,
        // before browsers protect the drag data after this event returns.
        const files = await collectDroppedDocumentFiles(event.dataTransfer);
        await uploadFilesToSharedFolder(files);
    };
    
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            alert('Please enter a folder name');
            return;
        }
        
        try {
            const response = await fetch(`${SHARED_VIEWER_API_ENDPOINT}?token=${token}&create_folder=1&path=${encodeURIComponent(currentPath)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    folder_name: newFolderName.trim()
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                fetchShareData();
                setNewFolderName('');
                setShowCreateFolder(false);
            } else {
                alert('Failed to create folder: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Create folder error:', error);
            alert('Failed to create folder: ' + error.message);
        }
    };
    
    // Format file size
    const formatSize = (size) => {
        if (!size) return 'Unknown size';
        return size < 1024 * 1024 
            ? `${(size / 1024).toFixed(1)} KB` 
            : `${(size / 1024 / 1024).toFixed(1)} MB`;
    };
    
    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return [
            date.toLocaleDateString(),
            date.toLocaleTimeString(),
            date.toLocaleString()
        ];
    };

    const getSavedSelectionIndex = (row, savedSelectionItems = []) => {
        if (!Array.isArray(savedSelectionItems) || savedSelectionItems.length === 0) {
            return row.originalIndex;
        }

        const rowKey = row.data?.key || '';
        const rowName = row.name || '';
        let bestIndex = Number.MAX_SAFE_INTEGER;

        savedSelectionItems.forEach((item, index) => {
            if (typeof item !== 'string') {
                return;
            }

            if (item === rowKey || item === rowName) {
                bestIndex = Math.min(bestIndex, index);
                return;
            }

            if (row.type === 'folder' && rowKey && item.startsWith(rowKey)) {
                bestIndex = Math.min(bestIndex, index);
            }
        });

        return bestIndex === Number.MAX_SAFE_INTEGER
            ? Number.MAX_SAFE_INTEGER - 1000 + row.originalIndex
            : bestIndex;
    };
    
    // Handle file preview for single file view
    const handleFilePreview = async (file) => {
        setPreviewLoading(true);
        setCurrentFile(file);
        
        const fileType = file.name.split('.').pop().toLowerCase();
        const previewUrl = getPreviewUrl(file);
        const downloadUrl = getDownloadUrl(file);
        
        try {
            let preview;
            
            if (fileType === "pdf") {
                preview = (
                    <div className="pdf-preview-container">
                        <div className="preview-header">
                            <h3>{file.name}</h3>
                        </div>
                        <iframe
                            src={previewUrl}
                            title={file.name}
                            width="100%"
                            height="500px"
                            className="pdf-preview"
                        />
                        <div className="preview-actions">
                            <button 
                                className="action" 
                                onClick={() => {
                                    openFileViewer(file);
                                }}
                            >
                                <Eye size={16} /> Full View
                            </button>
                            <button 
                                className="action alt" 
                                onClick={() => handleFileDownload(downloadUrl, file.name)}
                            >
                                <Download size={16} /> Download
                            </button>
                        </div>
                    </div>
                );
            } else if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileType)) {
                preview = (
                    <div className="image-preview-container">
                        <div className="preview-header">
                            <h3>{file.name}</h3>
                        </div>
                        <img 
                            src={previewUrl} 
                            alt={file.name}
                            style={{ width: "100%", height: "auto", maxHeight: "400px", objectFit: "contain" }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="image-fallback" style={{ display: 'none' }}>
                            {renderSharedFileTypeBadge('image')}
                        </div>
                        <div className="file-info">
                            <p><strong>Type:</strong> {fileType.toUpperCase()} Image</p>
                            <p><strong>Size:</strong> {formatSize(file.size)}</p>
                            {file.last_modified && <p><strong>Modified:</strong> {formatDate(file.last_modified)[0]} at {formatDate(file.last_modified)[1]}</p>}
                        </div>
                        <div className="preview-actions">
                            <button 
                                className="action" 
                                onClick={() => {
                                    openFileViewer(file);
                                }}
                            >
                                <Eye size={16} /> Full View
                            </button>
                            <button 
                                className="action alt" 
                                onClick={() => handleFileDownload(downloadUrl, file.name)}
                            >
                                <Download size={16} /> Download
                            </button>
                        </div>
                    </div>
                );
            } else if (["mp3", "wav", "ogg", "aac", "flac"].includes(fileType)) {
                preview = (
                    <div className="audio-preview-container">
                        <div className="preview-header">
                            <h3>{file.name}</h3>
                        </div>
                        <audio 
                            controls 
                            src={previewUrl} 
                            style={{ width: "100%", marginBottom: "15px" }}
                            preload="metadata"
                        />
                        <div className="file-info">
                            <p><strong>Type:</strong> Audio file ({fileType.toUpperCase()})</p>
                            <p><strong>Size:</strong> {formatSize(file.size)}</p>
                            {file.last_modified && <p><strong>Modified:</strong> {formatDate(file.last_modified)[0]} at {formatDate(file.last_modified)[1]}</p>}
                        </div>
                        <div className="preview-actions">
                            <button 
                                className="action" 
                                onClick={() => {
                                    openFileViewer(file);
                                }}
                            >
                                <Eye size={16} /> Full View
                            </button>
                            <button 
                                className="action alt" 
                                onClick={() => handleFileDownload(downloadUrl, file.name)}
                            >
                                <Download size={16} /> Download
                            </button>
                        </div>
                    </div>
                );
            } else if (["mp4", "webm", "mov"].includes(fileType)) {
                preview = (
                    <div className="video-preview-container">
                        <div className="preview-header">
                            <h3>{file.name}</h3>
                        </div>
                        <video 
                            controls 
                            src={previewUrl} 
                            style={{ width: "100%", maxHeight: "400px", marginBottom: "15px" }}
                            preload="metadata"
                        />
                        <div className="file-info">
                            <p><strong>Type:</strong> Video file ({fileType.toUpperCase()})</p>
                            <p><strong>Size:</strong> {formatSize(file.size)}</p>
                            {file.last_modified && <p><strong>Modified:</strong> {formatDate(file.last_modified)[0]} at {formatDate(file.last_modified)[1]}</p>}
                        </div>
                        <div className="preview-actions">
                            <button 
                                className="action" 
                                onClick={() => {
                                    openFileViewer(file);
                                }}
                            >
                                <Eye size={16} /> Full View
                            </button>
                            <button 
                                className="action alt" 
                                onClick={() => handleFileDownload(downloadUrl, file.name)}
                            >
                                <Download size={16} /> Download
                            </button>
                        </div>
                    </div>
                );
            } else if (["txt", "csv", "json", "log", "md"].includes(fileType)) {
                // For text files, fetch the content
                try {
                    const textResponse = await fetch(previewUrl, { credentials: 'include' });
                    if (!textResponse.ok) {
                        throw new Error(`Preview failed with HTTP ${textResponse.status}`);
                    }
                    const textContent = await textResponse.text();
                    const truncatedText = textContent.length > 3000 ? textContent.substring(0, 3000) + '...' : textContent;

                    preview = (
                        <div className="text-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <div className="text-preview" style={{ 
                                padding: "15px", 
                                whiteSpace: "pre-wrap", 
                                fontSize: "12px",
                                lineHeight: "1.4",
                                backgroundColor: "var(--secondary-color)",
                                border: "1px solid var(--border-color)",
                                borderRadius: '6px',
                                maxHeight: "400px",
                                overflowY: "auto",
                                fontFamily: "monospace"
                            }}>
                                {truncatedText}
                            </div>
                            {textContent.length > 3000 && (
                                <p className="truncation-notice">Content truncated. Full content available in full view.</p>
                            )}
                            <div className="file-info">
                                <p><strong>Type:</strong> {fileType.toUpperCase()} file</p>
                                <p><strong>Size:</strong> {formatSize(file.size)}</p>
                                {file.last_modified && <p><strong>Modified:</strong> {formatDate(file.last_modified)[0]} at {formatDate(file.last_modified)[1]}</p>}
                            </div>
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => {
                                        openFileViewer(file);
                                    }}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(downloadUrl, file.name)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                } catch (textError) {
                    console.error('Error loading text content:', textError);
                    preview = renderUnsupportedFilePreview(fileType, 'Preview is not available for this text file.');
                }
            } else {
                // Generic file preview
                preview = renderUnsupportedFilePreview(fileType);
            }
            
            setFilePreview(preview);
        } catch (error) {
            console.error('Error loading file preview:', error);
            setFilePreview(
                <div className="preview-error">
                    <p>Failed to load file preview.</p>
                </div>
            );
        } finally {
            setPreviewLoading(false);
        }
    };
    
    // Function to navigate to the source folder in case documents
    const navigateToSourceFolder = () => {
        if (!shareData || !shareData.case_id || !shareData.target_key) return;
        
        // Extract folder path from target_key
        // target_key format: "cases/CASE_ID/{{Name}}/folder/path/filename.ext"
        const targetKey = shareData.target_key;
        const keyParts = targetKey.split('/');
        
        // Find the index of {{Name}} and get everything after it except the filename
        const nameIndex = keyParts.findIndex(part => part === '{{Name}}');
        if (nameIndex === -1) return;
        
        // Get folder path (everything between {{Name}} and filename)
        const folderParts = keyParts.slice(nameIndex + 1, -1); // Exclude filename
        const folderPath = folderParts.join('/');
        
        // Get the filename
        const fileName = keyParts[keyParts.length - 1];
        
        // Construct the case documents URL
        const caseUrl = `/case/${shareData.case_id}?section=3`;
        let fullUrl = caseUrl;
        
        // Add folder parameter if there's a folder path
        if (folderPath) {
            fullUrl += `&folder=${encodeURIComponent(folderPath)}`;
        }
        
        // Add file parameter to automatically open/highlight the file
        if (fileName) {
            fullUrl += `&file=${encodeURIComponent(fileName)}`;
        }
        
        // Navigate to the case documents
        window.open(fullUrl, '_blank');
    };
    
    if (passwordPrompt) {
        return (
            <div className="page-container shared-viewer-container">
                <div className="shared-viewer-password" style={{ maxWidth: 400, margin: '80px auto', padding: 24, background: 'var(--secondary-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                    <h2 style={{ marginTop: 0 }}>Protected Share</h2>
                    <div className='form-group full'>
                        <label className='subtext'>A password is required to view this shared content.</label>
                        <input
                            type="password"
                            autoFocus
                            autoComplete="current-password"
                            placeholder="Enter password"
                            value={passwordValue}
                            onChange={e => {
                                setPasswordValue(e.target.value);
                                setBadPassword(false);
                                setPasswordError('');
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && passwordValue && !loading) {
                                    fetchShareData({ password: passwordValue });
                                }
                            }}
                        />
                    </div>
                    {badPassword && (
                        <p className='subtext' role="alert" style={{ color: 'tomato', marginTop: 8 }}>
                            {passwordError || 'Incorrect password. Try again.'}
                        </p>
                    )}
                    {error && <p role="alert" className='subtext' style={{ color: 'tomato', marginTop: 8 }}>{error}</p>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                        <button className='action' disabled={!passwordValue || loading} onClick={() => fetchShareData({ password: passwordValue })}>{loading ? 'Unlocking…' : 'Unlock'}</button>
                        <button className='action alt' onClick={() => navigate('/')}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-container shared-viewer-container">
                <div className="shared-viewer-loading">Loading shared content...</div>
            </div>
        );
    }
    
    if (error || !shareData) {
        return (
            <div className="page-container shared-viewer-container">
                <div className="shared-viewer-error">
                    <h2>Error</h2>
                    <p>{error || "The shared link is invalid or has expired"}</p>
                    <button onClick={() => navigate('/')} className="action">Go to Home</button>
                </div>
            </div>
        );
    }
    
    const { files, folders, is_folder, single_file } = shareData;
    const savedSelectionItems = Array.isArray(shareData.selected_items) ? shareData.selected_items : [];
    const baseRows = [
        ...(folders || []).map((folder, index) => ({
            id: `folder:${currentPath || ''}/${folder.name}`.replace(/\/+/g,'/'),
            type: 'folder',
            name: folder.name,
            data: folder,
            originalIndex: index
        })),
        ...(files || []).map((file, index) => ({
            id: `file:${currentPath || ''}/${file.name}`.replace(/\/+/g,'/'),
            type: 'file',
            name: file.name,
            url: file.url,
            size: file.size,
            data: file,
            originalIndex: (folders || []).length + index
        }))
    ];
    const currentRows = [...baseRows].sort((left, right) => {
        if (nameSortDirection) {
            const comparison = left.name.localeCompare(right.name, undefined, { sensitivity: 'base', numeric: true });
            return nameSortDirection === 'asc' ? comparison : -comparison;
        }

        const leftIndex = getSavedSelectionIndex(left, savedSelectionItems);
        const rightIndex = getSavedSelectionIndex(right, savedSelectionItems);
        if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
        }

        return left.originalIndex - right.originalIndex;
    });
    const fileList = currentRows.filter((row) => row.type === 'file').map((row) => row.data);
    const toggleNameSort = () => {
        setNameSortDirection((current) => {
            if (current === null) return 'asc';
            if (current === 'asc') return 'desc';
            return null;
        });
    };
    const nameSortLabel = nameSortDirection === 'asc'
        ? 'Name ↑'
        : nameSortDirection === 'desc'
            ? 'Name ↓'
            : 'Name';

    const navigateViewer = (direction) => {
        if (!fileList.length) return;
        const currentKey = currentFile?.key || currentFile?.name;
        let index = fileList.findIndex((f) => f.key === currentKey || f.name === currentKey);
        if (index < 0) index = 0;
        const nextIndex = direction === 'prev'
            ? (index - 1 + fileList.length) % fileList.length
            : (index + 1) % fileList.length;
        openFileViewer(fileList[nextIndex]);
    };

    // Helpers for selection
    const isSelected = (id) => selectedItems.some(it => it.id === id);
    const toggleSelect = (row) => {
        setSelectedItems(prev => isSelected(row.id) ? prev.filter(it => it.id !== row.id) : [...prev, row]);
    };
    const toggleSelectAll = () => {
        if (selectedItems.length === currentRows.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(currentRows);
        }
    };

    const handleBulkIndividualDownload = async () => {
        const fileRows = selectedItems.filter(it => it.type === 'file');
        for (const row of fileRows) {
            await handleFileDownload(row.data?.download_url || row.url, row.name);
        }
    };

    const getSafeZipFileName = (name = 'documents') => {
        const baseName = String(name || 'documents')
            .replace(/\.zip$/i, '')
            .replace(/[<>:"/\\|?*]/g, '_')
            .split('')
            .filter((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)
            .join('')
            .replace(/\s+/g, ' ')
            .replace(/\.+$/g, '')
            .trim()
            .slice(0, 120) || 'documents';
        return `${baseName}.zip`;
    };

    const startSharedZipDownload = async (rows, requestedName = 'documents.zip') => {
        // Include both files and folders in selection
        const rowsToZip = Array.isArray(rows) ? rows : [];
        const selectedFiles = rowsToZip.filter(it => it.type === 'file');
        const selectedFolders = rowsToZip.filter(it => it.type === 'folder');
        const zipFileName = getSafeZipFileName(requestedName);
        
        if (rowsToZip.length === 0) return;
        
        setBuildingZip(true);
        setZipProgress(0);
        setZipStatus('Starting ZIP creation...');
        setZipReadyDownload(null);
        const pollGeneration = zipPollGenerationRef.current + 1;
        zipPollGenerationRef.current = pollGeneration;
        
        try {
            let keys = [];

                // Always pass explicit selected item keys only
                selectedFolders.forEach(row => {
                    const folderKey = row.data?.key || null;
                    if (folderKey) {
                        keys.push(folderKey);
                    }
                });

                selectedFiles.forEach(row => {
                    const fileKey = row.data?.key || null;
                    if (fileKey) {
                        keys.push(fileKey);
                    }
                });

            console.log('Shared ZIP selection: using explicit item keys', {
                selectedFiles: selectedFiles.length,
                selectedFolders: selectedFolders.length,
                totalKeys: keys.length
            });
            
            // Ensure {{Name}} is present in keys for S3 compatibility (like DocumentSection does)
            keys = keys.map(key => {
                if (shareData.case_id) {
                    const casePrefix = `cases/${shareData.case_id}/`;
                    if (key.startsWith(casePrefix) && !key.includes('{{Name}}')) {
                        // Insert {{Name}}/ right after cases/{case_id}/
                        const afterCaseId = key.substring(casePrefix.length);
                        return `${casePrefix}{{Name}}/${afterCaseId}`;
                    }
                }
                return key;
            });
            
            setZipStatus('Requesting ZIP creation...');
            
            // Start the server-side ZIP creation using documents.php (same as DocumentSection)
            // Note: This may require backend modification to support shared token authentication
            const zipResponse = await fetch(`${DOCUMENTS_API_ENDPOINT}?zip&shared_token=${encodeURIComponent(token)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    keys: keys,
                    case_id: shareData.case_id,
                    current_path: currentPath || '',
                    download_name: zipFileName
                }),
                credentials: 'include'
            });
            
            const zipResult = await zipResponse.json();
            
            if (!zipResponse.ok || !zipResult.success) {
                throw new Error(zipResult.message || 'Failed to start ZIP creation');
            }
            
            const taskId = zipResult.task_id;
            setZipStatus('ZIP creation in progress...');
            
            let transientFailures = 0;
            while (zipPollGenerationRef.current === pollGeneration) {
                try {
                    const statusResponse = await fetch(
                        `${DOCUMENTS_API_ENDPOINT}?zip_status=${encodeURIComponent(taskId)}&shared_token=${encodeURIComponent(token)}`,
                        { credentials: 'include' }
                    );
                    let status;
                    try {
                        status = await statusResponse.json();
                    } catch (_) {
                        status = null;
                    }

                    if (!statusResponse.ok) {
                        const requestError = new Error(status?.message || `ZIP status request failed (${statusResponse.status})`);
                        requestError.terminal = [400, 401, 403, 404, 410, 422].includes(statusResponse.status);
                        throw requestError;
                    }
                    if (!status?.success) {
                        const statusError = new Error(status?.message || 'ZIP status is unavailable');
                        statusError.terminal = true;
                        throw statusError;
                    }

                    transientFailures = 0;
                    if (status.status === 'complete') {
                        const downloadUrl = status.download_url
                            || `${DOCUMENTS_API_ENDPOINT}?zip_download=${encodeURIComponent(taskId)}&shared_token=${encodeURIComponent(token)}`;
                        const readyDownload = { url: downloadUrl, name: zipFileName };
                        setZipReadyDownload(readyDownload);
                        setZipProgress(100);
                        setZipStatus('ZIP ready. Download started.');
                        const link = document.createElement('a');
                        link.href = readyDownload.url;
                        link.download = readyDownload.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return;
                    }
                    if (status.status === 'error' || status.status === 'cancelled') {
                        const terminalError = new Error(status.message || 'ZIP creation failed on server');
                        terminalError.terminal = true;
                        throw terminalError;
                    }

                    const progress = Number.isFinite(Number(status.progress)) ? Number(status.progress) : 0;
                    setZipProgress(progress);
                    setZipStatus(status.message || `Creating ZIP... ${progress}%`);
                    await new Promise(resolve => setTimeout(resolve, 2000));
                } catch (pollError) {
                    if (zipPollGenerationRef.current !== pollGeneration) return;
                    if (pollError.terminal) throw pollError;
                    transientFailures += 1;
                    const retryDelay = Math.min(30000, 2000 * (2 ** Math.min(transientFailures - 1, 4)));
                    console.warn('ZIP status connection interrupted; retrying', pollError);
                    setZipStatus(`Connection interrupted. Retrying in ${Math.ceil(retryDelay / 1000)} seconds...`);
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }
            
        } catch (error) {
            if (zipPollGenerationRef.current !== pollGeneration) return;
            console.error('Error creating ZIP:', error);
            setZipStatus(error.message || 'ZIP creation failed');
            alert(`Failed to create ZIP: ${error.message}`);
        } finally {
            if (zipPollGenerationRef.current === pollGeneration) {
                setBuildingZip(false);
            }
        }
    };

    const retryReadyZipDownload = () => {
        if (!zipReadyDownload) return;
        const link = document.createElement('a');
        link.href = zipReadyDownload.url;
        link.download = zipReadyDownload.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBulkZipDownload = async () => {
        const bulkName = selectedItems.length === 1
            ? selectedItems[0]?.name || 'shared-documents'
            : 'shared-documents';
        await startSharedZipDownload(selectedItems, bulkName);
    };

    const handleFolderZipDownload = async (row) => {
        await startSharedZipDownload([row], row?.name || row?.data?.name || 'shared-folder');
    };
    
    // For single file shares
    if (!is_folder && single_file) {
        const singleFileType = single_file.name?.split('.').pop()?.toUpperCase() || 'FILE';
        const singleFileDate = single_file.last_modified ? formatDate(single_file.last_modified) : null;

        return (
            <div className={`shared-viewer-container shared-viewer-single-file-container ${addMargin ? 'page-container' : ''} ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
                <div className="shared-viewer-single-file">
                    <div className="shared-single-file-header">
                        <div className="shared-single-file-title-group">
                            <h2 title={single_file.name}>{single_file.name}</h2>
                            <div className="shared-single-file-meta subtext">
                                <span>{singleFileType}</span>
                                {single_file.size !== undefined && <span>{formatSize(single_file.size)}</span>}
                                {singleFileDate && <span>{singleFileDate[0]} at {singleFileDate[1]}</span>}
                                {shareData.is_expired && <span>Expired link · Internal review</span>}
                            </div>
                        </div>
                        <div className="shared-single-file-actions">
                            <button
                                className="action alt"
                                onClick={() => handleFileDownload(single_file.download_url || single_file.url, single_file.name)}
                                title="Download file"
                            >
                                <Download size={16} /> Download
                            </button>
                            {addMargin && shareData.case_id && shareData.target_key && (
                                <button
                                    className="action tertiary"
                                    onClick={navigateToSourceFolder}
                                    title="Open in case documents"
                                    style={{ backgroundColor: 'var(--secondary-color)' }}
                                >
                                    <ExternalLink size={16} />
                                    Open in Case
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {previewLoading && (
                        <div className="loading-preview">
                            <Loader2 className="animate-spin" />
                            <span>Loading file...</span>
                        </div>
                    )}
                    
                    {filePreview && (
                        <div className="shared-file-preview">
                            {filePreview}
                        </div>
                    )}
                </div>
                
                {/* Document Viewer Modal */}
                {showDocumentViewer && currentFile && (
                    <DocumentViewer
                        file={currentFile}
                        onClose={() => {
                            setShowDocumentViewer(false);
                            setCurrentFile(null);
                        }}
                        onPrev={() => navigateViewer('prev')}
                        onNext={() => navigateViewer('next')}
                    />
                )}
            </div>
        );
    }
    
    return (
        <div
            className={`shared-viewer-container ${addMargin ? 'page-container' : ''} ${dragActive ? 'shared-viewer-drop-active' : ''}`}
            role={shareData.allow_upload ? 'region' : undefined}
            aria-label={shareData.allow_upload ? 'Shared folder upload area' : undefined}
            onDragEnter={handleSharedDragEnter}
            onDragOver={handleSharedDragOver}
            onDragLeave={handleSharedDragLeave}
            onDrop={handleSharedDrop}
        >
            <div className="shared-viewer-header">
                <h2>Shared Content</h2>
                {shareData.expires_at && (
                    <p className="shared-viewer-expiry">
                        {shareData.is_expired ? 'Expired' : 'Link expires'}: {new Date(shareData.expires_at).toLocaleDateString()}
                    </p>
                )}
            </div>

            {shareData.is_expired && (
                <div className="shared-viewer-expired-notice">
                    This expired link is available for internal review and is limited to its recorded files.
                </div>
            )}
            
            {shareData.success ? (
                <>
                    {selectedItems.length > 0 && (
                        <div className="shared-selection-bar" style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'var(--secondary-color)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            marginBottom: 12
                        }}>
                            <span className='subtext'>{selectedItems.length} selected</span>
                            <button className='action quaternary' onClick={handleBulkIndividualDownload} disabled={buildingZip} title='Download each file'>
                                <Download size={16} /> Individual
                            </button>
                            <button className='action quaternary' onClick={handleBulkZipDownload} disabled={buildingZip} title='Download as ZIP'>
                                {buildingZip ? <Loader2 size={16} className='spinner' /> : <FileArchive size={16} />} ZIP
                            </button>
                            <button className='action quaternary' onClick={() => setSelectedItems([])} disabled={buildingZip} title='Clear selection'>
                                <X size={16} /> Clear
                            </button>
                            {selectedItems.length !== currentRows.length && (
                                <button className='action quaternary' onClick={toggleSelectAll} disabled={buildingZip} title='Select all'>
                                    <Square size={16} /> Select All
                                </button>
                            )}
                            {buildingZip && (
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className='subtext' style={{ fontSize: 12, minWidth: 'fit-content' }}>{zipStatus}</span>
                                    <div className='upload-progress-bar' style={{ flex: 1, height: 6, background: 'var(--border-color)', borderRadius: 4 }}>
                                        <div style={{ width: `${zipProgress}%`, background: 'var(--text-color)', height: '100%', borderRadius: 4 }} />
                                    </div>
                                    <span className='subtext' style={{ fontSize: 12 }}>{zipProgress}%</span>
                                </div>
                            )}
                        </div>
                    )}
                    {buildingZip && selectedItems.length === 0 && (
                        <div className="shared-selection-bar" style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'var(--secondary-color)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            marginBottom: 12
                        }}>
                            <FileArchive size={16} />
                            <span className='subtext' style={{ fontSize: 12, minWidth: 'fit-content' }}>{zipStatus}</span>
                            <div className='upload-progress-bar' style={{ flex: 1, height: 6, background: 'var(--border-color)', borderRadius: 4 }}>
                                <div style={{ width: `${zipProgress}%`, background: 'var(--text-color)', height: '100%', borderRadius: 4 }} />
                            </div>
                            <span className='subtext' style={{ fontSize: 12 }}>{zipProgress}%</span>
                        </div>
                    )}
                    {zipReadyDownload && !buildingZip && (
                        <div className="shared-selection-bar" style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'var(--secondary-color)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            marginBottom: 12
                        }}>
                            <FileArchive size={16} />
                            <span className="subtext" style={{ flex: 1 }}>
                                ZIP ready. You can safely retry or resume the download.
                            </span>
                            <button
                                type="button"
                                className="action quaternary"
                                onClick={retryReadyZipDownload}
                            >
                                <Download size={16} /> Download ZIP again
                            </button>
                        </div>
                    )}
                    <div className="shared-viewer-breadcrumbs subtext">
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <ChevronRight size={16} />}
                                <span
                                    className={index === breadcrumbs.length - 1 ? "current-crumb" : "clickable-crumb"}
                                    onClick={() => {
                                        if (index !== breadcrumbs.length - 1) {
                                            handleBreadcrumbClick(crumb.path);
                                        }
                                    }}
                                >
                                    {crumb.name}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                    
                    {shareData.allow_upload && (
                        <div className="shared-viewer-actions">
                            <button 
                                className="action tertiary" 
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                title='Upload File(s)'
                            >
                                <Upload size={16} style={{ marginRight: '5px' }} />Upload File(s)
                            </button>
                            
                            <button 
                                className="action tertiary" 
                                onClick={() => folderInputRef.current?.click()}
                                disabled={uploading}
                                title='Upload Folder'
                            >
                                <FolderOpenIcon size={16} style={{ marginRight: '5px' }} />Upload Folder
                            </button>
                            
                            <button 
                                className="action quaternary" 
                                onClick={() => setShowCreateFolder(true)}
                                disabled={uploading}
                                title='Create New Folder'
                            >
                                <FolderPlus size={16} style={{ marginRight: '5px' }} />Create New Folder
                            </button>
                            
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                aria-label="Choose files to upload"
                                style={{ display: 'none' }}
                                onChange={handleUploadPickerChange}
                            />
                            
                            <input
                                ref={folderInputRef}
                                type="file"
                                webkitdirectory="true"
                                directory="true"
                                multiple
                                aria-label="Choose a folder to upload"
                                style={{ display: 'none' }}
                                onChange={handleUploadPickerChange}
                            />
                            <span className="shared-viewer-drop-copy subtext">or drag and drop files or folders</span>
                        </div>
                    )}

                    {dragActive && (
                        <div className="shared-viewer-drop-prompt" role="status">
                            Drop files or folders to upload them here
                        </div>
                    )}
                    
                    {uploading && (
                        <div className="upload-progress-container">
                            <p style={{ color: 'var(--text-color)' }}>Uploading... {uploadProgress}%</p>
                            <div className="upload-progress-bar">
                                <div 
                                    className="upload-progress-fill" 
                                    style={{ width: `${uploadProgress}%`, backgroundColor: 'var(--text-color)' }}
                                ></div>
                            </div>
                        </div>
                    )}
                    
                    {showCreateFolder && (
                        <Modal
                            title="Create New Folder"
                            footer={
                                <div className="modal-footer-actions">
                                    <button onClick={handleCreateFolder} className="action">Create</button>
                                    <button onClick={() => setShowCreateFolder(false)} className="action alt">Cancel</button>
                                </div>
                            }
                        >
                            <div className="modal-content-wrapper">
                                <div className="form-group mid">
                                    <label className="subtext">Folder Name</label>
                                    <input
                                        type="text"
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        placeholder="Enter folder name"
                                    />
                                </div>
                            </div>
                        </Modal>
                    )}
                    
                    <div className="shared-viewer-content">
                        {folders?.length === 0 && files?.length === 0 ? (
                            <p>This folder is empty</p>
                        ) : (
                            <table className="shared-viewer-table">
                                <thead>
                                    <tr>
                                        <th className='shared-viewer-name subtext'>
                                            <div onClick={toggleSelectAll} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', marginRight: '15px' }} title={selectedItems.length === currentRows.length ? 'Unselect all' : 'Select all'}>
                                                {selectedItems.length === currentRows.length && currentRows.length > 0 ? <CheckSquare size={16} style={{ color: 'var(--fill)' }} /> : <Square size={16} />}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={toggleNameSort}
                                                title={nameSortDirection ? 'Click to change name sort' : 'Click to sort alphabetically'}
                                                style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, font: 'inherit' }}
                                            >
                                                {nameSortLabel}
                                            </button>
                                        </th>
                                        <th className='shared-viewer-date subtext'>Last Modified</th>
                                        <th className='shared-viewer-size subtext'>Size</th>
                                        <th className='shared-viewer-action subtext'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRows.map((row) => {
                                        if (row.type === 'folder') {
                                            const folder = row.data;
                                            return (
                                                <tr key={row.id} className={`shared-viewer-item${isSelected(row.id) ? ' selected' : ''}`}> 
                                                    <td 
                                                        className="shared-viewer-name folder subtext"
                                                        onClick={() => handleFolderClick(folder.name)}
                                                    >
                                                        <div onClick={(e) => { e.stopPropagation(); toggleSelect(row); }} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', marginRight: "10px" }} title={isSelected(row.id) ? 'Unselect' : 'Select'}>
                                                            {isSelected(row.id) ? <CheckSquare size={16} style={{ color: 'var(--fill)' }} /> : <Square size={16} />}
                                                        </div>
                                                        <FolderOpenIcon size={16} style={{ marginRight: 6 }} />
                                                        <span style={{ textAlign: "left", textWrap: "wrap" }}>{folder.name}</span>
                                                    </td>
                                                    <td className='shared-viewer-date subtext'>{folder.last_modified ? new Date(folder.last_modified).toLocaleDateString() : '-'}</td>
                                                    <td className='shared-viewer-size subtext'>
                                                        {folder.items_complete === false && (folder.items === null || folder.items === undefined)
                                                            ? 'Open to count'
                                                            : `${Number(folder.items || 0)} items`}
                                                    </td>
                                                    <td className='shared-viewer-action subtext'>
                                                        <button
                                                            className="shared-viewer-download"
                                                            onClick={(e) => { e.stopPropagation(); handleFolderZipDownload(row); }}
                                                            title="Download folder as ZIP"
                                                            disabled={buildingZip}
                                                        >
                                                            {buildingZip ? <Loader2 size={16} className="spinner" /> : <FileArchive size={16} />}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        const file = row.data;
                                        return (
                                            <tr
                                                key={row.id}
                                                className={`shared-viewer-item${isSelected(row.id) ? ' selected' : ''}`}
                                                onClick={() => openFileViewer(file)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="shared-viewer-name subtext" onClick={() => openFileViewer(file)}>
                                                    <div
                                                        onClick={(e) => { e.stopPropagation(); toggleSelect(row); }}
                                                        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', marginRight: "10px" }}
                                                        title={isSelected(row.id) ? 'Unselect' : 'Select'}
                                                    >
                                                        {isSelected(row.id) ? <CheckSquare size={16} style={{ color: 'var(--fill)' }} /> : <Square size={16} />}
                                                    </div>
                                                    <FileSvg size={16} style={{ marginRight: 6 }} />
                                                    {file.name}
                                                </td>
                                                <td className='shared-viewer-date subtext'>{file.last_modified ? new Date(file.last_modified).toLocaleDateString() : '-'}</td>
                                                <td className='shared-viewer-size subtext'>{file.size ? formatSize(file.size) : '-'}</td>
                                                <td className='shared-viewer-action subtext'>
                                                    <button 
                                                        className="shared-viewer-view"
                                                        onClick={() => {
                                                            openFileViewer(file);
                                                        }}
                                                        title="View file"
                                                        style={{ marginRight: '8px' }}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button 
                                                        className="shared-viewer-download"
                                                        onClick={(e) => { e.stopPropagation(); handleFileDownload(file.download_url || file.url, file.name); }}
                                                        title="Download file"
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            ) : (
                <>{shareData.message}</>
            )}
            
            {/* Document Viewer Modal */}
            {showDocumentViewer && currentFile && (
                <DocumentViewer
                    file={currentFile}
                    onClose={() => {
                        setShowDocumentViewer(false);
                        setCurrentFile(null);
                    }}
                    onPrev={() => navigateViewer('prev')}
                    onNext={() => navigateViewer('next')}
                />
            )}
        </div>
    );
};

export default SharedViewer;
